import { useCallback } from 'react';
import { useTestContext } from '../../context/testContext';
import { useCalibration } from '../../context/calibrationContext';

const boundaries = { x: window.screen.width, y: window.screen.height };

export const useDotPosition = () => {
  const {
    testData: { visualFieldSettings },
  } = useTestContext();
  const {
    calibrationData: { blindSpotVanishingXDistancePx },
  } = useCalibration();

  const fixationSize = { x: visualFieldSettings?.fixationTargetDimensions?.width, y: visualFieldSettings?.fixationTargetDimensions?.height };
  const minCoord = { x: boundaries.x * visualFieldSettings?.screenBuffer, y: boundaries.y * visualFieldSettings?.screenBuffer };
  const maxCoord = { x: boundaries.x - minCoord.x - fixationSize.x, y: boundaries.y - minCoord.y - fixationSize.y };

  const getDegreesInPx = useCallback(
    (dot) => {
      const distanceInPx = ['x', 'y'].reduce((acc, axis) => {
        const degrees = dot[`degree${axis.toUpperCase()}`];
        const px = Math.floor((degrees * blindSpotVanishingXDistancePx) / 15);
        return { ...acc, [axis]: axis === 'y' ? -px : px };
      }, {});

      return distanceInPx;
    },
    [blindSpotVanishingXDistancePx],
  );

  const adjustPositions = ({ distancesPx, fixationTargetCoordinates }) => {
    const fixationCenter = { x: fixationTargetCoordinates.x + fixationSize.x / 2, y: fixationTargetCoordinates.y + fixationSize.y / 2 };

    const allCoords = ['x', 'y'].reduce((acc, coord) => {
      const proposedPosition = fixationCenter[coord] + distancesPx[coord];
      if (proposedPosition < minCoord[coord])
        return {
          ...acc,
          [`stimulus${coord.toUpperCase()}`]: minCoord[coord],
          [`fixation${coord.toUpperCase()}`]: minCoord[coord] - Math.min(distancesPx[coord], -distancesPx[coord]),
        };
      if (proposedPosition > maxCoord[coord])
        return {
          ...acc,
          [`stimulus${coord.toUpperCase()}`]: maxCoord[coord],
          [`fixation${coord.toUpperCase()}`]: maxCoord[coord] - distancesPx[coord],
        };
      return {
        ...acc,
        [`stimulus${coord.toUpperCase()}`]: proposedPosition,
        [`fixation${coord.toUpperCase()}`]: fixationTargetCoordinates[coord],
      };
    }, {});

    return allCoords;
  };

  const getCoordinates = ({ dot, fixationTargetCoordinates }) => {
    const distancesPx = getDegreesInPx(dot);
    const newCoords = adjustPositions({ distancesPx, fixationTargetCoordinates });
    return {
      newStimulusCoords: { x: newCoords.stimulusX, y: newCoords.stimulusY },
      newFixationCoords: { x: newCoords.fixationX, y: newCoords.fixationY },
    };
  };

  return getCoordinates;
};
